import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'lodash';
import Button from '../../../../_common/Form/Buttons/Button';
import classes from './statusForm.module.scss';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Select from '../../../../_common/Form/Selects/Select/Select';
import DOBPicker from '../../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import {
  deliveryPostalServicesData,
  scriptNoteOrAttachmentIds,
  sendDeliveryPriceRequest,
  showShippingLabel,
} from '../../../../../store/actions/script';
import utils from '../../../../../utils/utils';
import fedExLogo from '../../../../../assets/png/usps.png';

const deliveryOptions = [
  'UPS',
  'Fedex',
  'GSO',
  'Deliver-it',
  'US Postal',
  'Delivery Driver',
  'Pick Up',
];

const paymentOptions = [
  'Collect on Delivery',
  'Mail in Check',
  'Credit Card',
  'No Copay',
];

const weightUnitOptions = ['lb', 'oz', 'kg', 'g'];

const dimensionsUnitOptions = ['in', 'cm'];

const ScheduleStatus = ({
  changeStatus, currentTab, info, changeScriptInfo, patientId,
}) => {
  const dispatch = useDispatch();

  const usPostalResponseData = useSelector((store) => store.script.usPostalResponseData);

  const postalServicesData = useSelector((store) => store.script.postalServicesData);

  const [copyButtonStatus, setCopyButtonStatus] = useState('Copy');

  const [deliveryLoad, setDeliveryLoad] = useState(false);

  const [dateError, setDateError] = useState({});

  const [requiredError, setRequiredError] = useState({});

  // const [shippingRateData, setShippingRateData] = useState({});

  const [requiredErrorUsPostal, setRequiredErrorUsPostal] = useState({});

  const [formData, setFormData] = useState({
    shipOn: info.shipOn || '',
    deliveryMethod: info.deliveryMethod || '',
    trackNum: info.trackNum || '',
    ETA: info.ETA || '',
    paymentOption: info.paymentOption || '',
  });

  const [fromUsPostalData, setFormUsPostalData] = useState({
    patientId,
    weight: info.homeCare ? '6' : '',
    length: '',
    width: '',
    height: '',
    dimUnit: '',
    weightUnit: info.homeCare ? 'oz' : '',
  });

  const {
    shipOn, deliveryMethod, trackNum, ETA, paymentOption,
  } = formData;

  const {
    weight, length, height, weightUnit, dimUnit, width,
  } = fromUsPostalData;

  const checkError = (status, type) => {
    let hasError = false;
    if (type) {
      _.forEach(fromUsPostalData, (value, key) => {
        if (!value && key !== 'patientId') {
          setRequiredErrorUsPostal((prev) => ({ ...prev, [key]: 'This field is required' }));
          hasError = true;
        }
      });
    } else {
      _.forEach(formData, (value, key) => {
        if (!value && key !== 'trackNum') {
          setRequiredError((prev) => ({ ...prev, [key]: 'This field is required' }));
          hasError = true;
        }
      });

      if (formData.shipOn && formData.shipOn.length !== 10) {
        setDateError((prev) => ({ ...prev, shipOn: 'Invalid date' }));

        hasError = true;
      }

      if (status && status === 'QA' && (!formData.shipOn || formData.shipOn.length !== 10)) {
        setDateError((prev) => ({ ...prev, shipOn: 'Field is required' }));

        hasError = true;
      }

      if (formData.ETA && formData.ETA.length !== 10) {
        setDateError((prev) => ({ ...prev, ETA: 'Invalid date' }));

        hasError = true;
      }
    }

    return hasError;
  };

  const setStatus = (status) => {
    if (status !== 'Review' && checkError(status)) {
      toast.error('Please fill this fields');
    } else {
      const data = { status, ...formData };

      changeStatus(data);
    }
  };

  const setDate = (path, value) => {
    setFormData((prev) => ({ ...prev, [path]: value }));

    setDateError((prev) => {
      delete prev[path];

      return prev;
    });

    setRequiredError((prev) => {
      delete prev[path];

      return prev;
    });
  };

  const changeFormData = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));

    if (key === 'deliveryMethod') {
      dispatch(showShippingLabel(value));
    }

    setRequiredError((prev) => {
      delete prev[key];

      return prev;
    });
  };

  const changeUsPostalData = useCallback((key, value) => {
    setFormUsPostalData((prev) => ({ ...prev, [key]: value }));

    setRequiredErrorUsPostal((prev) => {
      delete prev[key];

      return prev;
    });
  }, [fromUsPostalData]);

  useEffect(() => {
    if (currentTab !== 'RX History') setCopyButtonStatus('Copy');
  }, [currentTab]);

  useEffect(() => {
    if (deliveryMethod) dispatch(showShippingLabel(deliveryMethod));
  }, []);

  const copy = (status) => {
    if (checkError()) {
      toast.error('Please fill this fields');
    } else {
      setCopyButtonStatus(status);
      dispatch(scriptNoteOrAttachmentIds([]));

      if (copyButtonStatus === 'Cancel') {
        changeScriptInfo({ scheduleIds: null });
      } else {
        const historyTabRef = document.getElementById('rx-history-id');

        historyTabRef?.click();
        changeScriptInfo({ scheduleIds: [] });
      }
    }
  };

  const onKeyPress = (e, isPaste) => {
    utils.keyPressOnlyNumber(e, isPaste);
  };

  const generateClick = useCallback(async () => {
    if (checkError(null, true)) {
      toast.error('Please fill this fields');
    } else {
      setDeliveryLoad(true);

      await dispatch(sendDeliveryPriceRequest(fromUsPostalData));

      setDeliveryLoad(false);
    }
  }, [fromUsPostalData]);

  return (
    <div className={`${classes.status_wrapper} ${deliveryMethod === 'US Postal' ? classes.postal : ''}`}>
      <p>Call the patient and schedule delivery.</p>

      <div className={classes.form}>
        <div style={{ margin: '7px auto ', width: 400 }}>
          <DOBPicker
            labelBG="#f6f8fa"
            wrapperClassName={classes.date_picker}
            label="Ship On"
            date={shipOn}
            hideClearButton
            dateFormat="YYYY-MM-DD"
            onChange={(date) => setDate('shipOn', date)}
            autoValidation={false}
            error={dateError.shipOn || requiredError.shipOn}
          />

          <Select
            labelBG="#f6f8fa"
            label="Delivery Method"
            options={deliveryOptions}
            onChange={(option) => changeFormData('deliveryMethod', option)}
            currentOption={deliveryMethod}
            error={requiredError.deliveryMethod}
          />

          {/* {deliveryMethod === 'US Postal' && ( */}
          {/* <div className={classes.us_postal}> */}
          {/*  <div className={classes.size_wrapper}> */}
          {/*    <Select */}
          {/*      labelBG="#f6f8fa" */}
          {/*      label="Weight Unit" */}
          {/*      options={weightUnitOptions} */}
          {/*      onChange={(option) => changeUsPostalData('weightUnit', option)} */}
          {/*      currentOption={weightUnit} */}
          {/*      error={requiredErrorUsPostal.weightUnit} */}
          {/*    /> */}

          {/*    <InputField */}
          {/*      labelBG="#f6f8fa" */}
          {/*      label="Weight" */}
          {/*      value={weight} */}
          {/*      onChange={(value) => changeUsPostalData('weight', value)} */}
          {/*      error={requiredErrorUsPostal.weight} */}
          {/*      onKeyPress={(e) => onKeyPress(e)} */}
          {/*      onPaste={(e) => onKeyPress(e, true)} */}
          {/*    /> */}
          {/*  </div> */}

          {/*  <div className={classes.size_wrapper}> */}
          {/*    <Select */}
          {/*      labelBG="#f6f8fa" */}
          {/*      label="Dimensions Unit" */}
          {/*      options={dimensionsUnitOptions} */}
          {/*      onChange={(option) => changeUsPostalData('dimUnit', option)} */}
          {/*      currentOption={dimUnit} */}
          {/*      error={requiredErrorUsPostal.dimUnit} */}
          {/*    /> */}

          {/*    <InputField */}
          {/*      labelBG="#f6f8fa" */}
          {/*      label="Length" */}
          {/*      value={length} */}
          {/*      onChange={(value) => changeUsPostalData('length', value)} */}
          {/*      error={requiredErrorUsPostal.length} */}
          {/*      onKeyPress={(e) => onKeyPress(e)} */}
          {/*      onPaste={(e) => onKeyPress(e, true)} */}
          {/*    /> */}
          {/*  </div> */}

          {/*  <div className={classes.size_wrapper}> */}
          {/*    <InputField */}
          {/*      labelBG="#f6f8fa" */}
          {/*      label="Width" */}
          {/*      value={width} */}
          {/*      onChange={(value) => changeUsPostalData('width', value)} */}
          {/*      error={requiredErrorUsPostal.width} */}
          {/*      onKeyPress={(e) => onKeyPress(e)} */}
          {/*      onPaste={(e) => onKeyPress(e, true)} */}
          {/*    /> */}

          {/*    <InputField */}
          {/*      labelBG="#f6f8fa" */}
          {/*      label="Height" */}
          {/*      value={height} */}
          {/*      onChange={(value) => changeUsPostalData('height', value)} */}
          {/*      error={requiredErrorUsPostal.height} */}
          {/*      onKeyPress={(e) => onKeyPress(e)} */}
          {/*      onPaste={(e) => onKeyPress(e, true)} */}
          {/*    /> */}
          {/*  </div> */}

          {/*  <Button */}
          {/*    title="Generate" */}
          {/*    className={classes.us_postal_btn} */}
          {/*    reverseColor */}
          {/*    onClick={generateClick} */}
          {/*    loading={deliveryLoad} */}
          {/*  /> */}
          {/* </div> */}
          {/* )} */}

          <InputField
            labelBG="#f6f8fa"
            label="Tracking Number"
            value={trackNum}
            onChange={(value) => changeFormData('trackNum', value)}
            error={requiredError.trackNum}
          />

          <DOBPicker
            labelBG="#f6f8fa"
            wrapperClassName={classes.date_picker}
            label="ETA"
            date={ETA}
            dateFormat="YYYY-MM-DD"
            onChange={(date) => setDate('ETA', date)}
            autoValidation={false}
            error={dateError.ETA || requiredError.ETA}
          />

          <Select
            labelBG="#f6f8fa"
            label="Payment Option"
            options={paymentOptions}
            onChange={(option) => changeFormData('paymentOption', option)}
            currentOption={paymentOption}
            error={requiredError.paymentOption}
          />

          <div className={classes.btns_wrapper}>
            <Button
              className={classes.max_content}
              title="Schedule"
              scheduleButton
              onClick={() => setStatus('QA')}
            />

            <Button
              className={classes.max_content}
              title="Flag for review"
              reviewButton
              onClick={() => setStatus('Review')}
            />

            <Button
              className={classes.copy}
              title={copyButtonStatus}
              buttonColor="#5F6C8E"
              clearButton={copyButtonStatus === 'Cancel'}
              onClick={() => copy(copyButtonStatus === 'Cancel' ? 'Copy' : 'Cancel')}
            />
          </div>
        </div>
      </div>

      {deliveryMethod === 'US Postal' && (
      <div className={classes.rate_container}>
        {usPostalResponseData
          .map((u) => (
            <div
              className={classes.rate_wrapper}
              key={u.service}
              onClick={() => dispatch(deliveryPostalServicesData(u))}
              role="button"
              tabIndex="0"
            >
              <div className={`${classes.rate} ${postalServicesData?.service === u.service ? classes.active : ''}`}>
                <div>
                  <img src={fedExLogo} alt="logo" />
                </div>

                <p>{u.service}</p>

                <h2>
                  Shipping Rate:
                  {`$${u.price}`}
                </h2>

              </div>
            </div>
          ))}

      </div>
      )}
    </div>
  );
};

ScheduleStatus.propTypes = {
  info: PropTypes.object.isRequired,
  currentTab: PropTypes.string.isRequired,
  changeStatus: PropTypes.func.isRequired,
  changeScriptInfo: PropTypes.func.isRequired,
  patientId: PropTypes.number.isRequired,
};

export default ScheduleStatus;
